import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Stack, Chip, useTheme, alpha, Card, useMediaQuery, GlobalStyles,
} from '@mui/material';
import { MessageCircle, Megaphone, FileText, Users, Zap, Settings as SettingsIcon } from 'lucide-react';
import { WA_GREEN, WA_TEAL } from '../components/whatsapp/waData';
import { WA_PANEL_MIN_H, useVisualViewportHeight } from '../components/whatsapp/WaLayout';
import { WaSettingsProvider } from '../components/whatsapp/WaSettingsContext';
import WhatsAppInbox from '../components/whatsapp/WhatsAppInbox';
import WhatsAppBroadcasts from '../components/whatsapp/WhatsAppBroadcasts';
import WhatsAppTemplates from '../components/whatsapp/WhatsAppTemplates';
import WhatsAppContacts from '../components/whatsapp/WhatsAppContacts';
import WhatsAppAutomation from '../components/whatsapp/WhatsAppAutomation';
import WhatsAppSettings from '../components/whatsapp/WhatsAppSettings';
import { waApi } from '../components/whatsapp/waApi';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { useWaSettings } from '../components/whatsapp/WaSettingsContext';
import { useSearchParams } from 'react-router-dom';

// Triage-first ordering: read & reply (Inbox), then who you talk to (Contacts),
// what you can send (Templates), bulk sends (Broadcasts), automation, config.
const TABS = [
  { key: 'inbox', label: 'Inbox', icon: MessageCircle, Component: WhatsAppInbox },
  { key: 'contacts', label: 'Contacts & Tags', icon: Users, Component: WhatsAppContacts },
  { key: 'templates', label: 'Templates', icon: FileText, Component: WhatsAppTemplates },
  { key: 'broadcasts', label: 'Broadcasts', icon: Megaphone, Component: WhatsAppBroadcasts },
  { key: 'automation', label: 'Auto Messages', icon: Zap, Component: WhatsAppAutomation },
  { key: 'settings', label: 'Settings', icon: SettingsIcon, Component: WhatsAppSettings },
];

const WhatsAppViewContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const vpHeight = useVisualViewportHeight();
  const { activeTab: tab, setActiveTab: setTab, inboxActiveId } = useWaSettings();
  const [status, setStatus] = useState({ connected: false, live: false });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const phone = searchParams.get('phone');
    const tabParam = searchParams.get('tab');
    if (phone) {
      setTab(0);
    } else if (tabParam) {
      const idx = TABS.findIndex((t) => t.key === tabParam || (tabParam === 'automations' && t.key === 'automation'));
      if (idx !== -1) setTab(idx);
    }
  }, [searchParams, setTab]);

  const Active = TABS[tab].Component;

  const isChatOpenOnMobile = isMobile && tab === 0 && inboxActiveId;
  // On the Inbox tab the hero is slimmed down (subtitle hidden, tighter padding)
  // so the conversation thread reclaims vertical space; other tabs keep the full
  // hero where the extra context reads as intentional.
  const isInbox = tab === 0;

  useEffect(() => {
    waApi.getStatus()
      .then((data) => {
        if (data) setStatus(data);
      })
      .catch(() => { /* silent fallback - keep unconnected */ });
  }, []);

  return (
    <Container maxWidth={false} disableGutters sx={{ width: '100%', px: isChatOpenOnMobile ? 0 : { xs: 1.5, sm: 2.5, md: 3.5 }, py: isChatOpenOnMobile ? 0 : { xs: 1, md: 1.5 } }}>
      {isChatOpenOnMobile && (
        <GlobalStyles
          styles={{
            // Hide only the top app bar so the chat is truly full-screen. We do
            // NOT hide .MuiDrawer-root: the nav drawer is already hidden on
            // mobile by its own breakpoint, and a blanket hide also swallowed
            // the in-chat contact ProfileDrawer (a real bug).
            'header.MuiAppBar-root': {
              display: 'none !important',
            },
            'nav[aria-label="Mobile Bottom Navigation"]': {
              display: 'none !important',
            },
            'main': {
              marginTop: '0px !important',
              marginBottom: '0px !important',
              paddingLeft: '0px !important',
              paddingRight: '0px !important',
              paddingTop: '0px !important',
              paddingBottom: '0px !important',
              height: vpHeight ? `${vpHeight}px !important` : '100dvh !important',
              maxWidth: '100% !important',
            },
            'body': {
              overflow: 'hidden !important',
            }
          }}
        />
      )}

      {/* Header Card with Premium Styling */}
      {!isChatOpenOnMobile && (
        <Card
          sx={{
            p: isInbox ? { xs: 1.25, sm: 1.75 } : { xs: 1.75, sm: 2.5 },
            mb: isInbox ? { xs: 1.25, sm: 2 } : { xs: 2, sm: 3 },
            borderRadius: { xs: 2.5, sm: 3 },
            background: theme.palette.mode === 'dark' 
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`
              : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fafc', 0.95)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px 0 rgba(0, 0, 0, 0.24)'
              : '0 8px 32px 0 rgba(145, 158, 171, 0.08)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={{ xs: 1.25, sm: 2 }}>
            <Box sx={{
              width: isInbox ? { xs: 36, sm: 40 } : 48,
              height: isInbox ? { xs: 36, sm: 40 } : 48,
              borderRadius: { xs: '12px', sm: '16px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${WA_GREEN} 0%, ${WA_TEAL} 100%)`,
              color: '#fff',
              boxShadow: `0 6px 20px ${alpha(WA_GREEN, 0.4)}`,
              flexShrink: 0,
              transition: 'transform 0.3s ease',
              '&:hover': { transform: 'scale(1.05)' }
            }}>
              <WhatsAppIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" sx={{ gap: 0.75 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif', fontSize: isInbox ? { xs: 17, sm: 22 } : { xs: 20, sm: 24 } }}>WhatsApp Portal</Typography>
                <Chip
                  label={status.connected ? (status.live ? 'Connected · Live' : 'Connected · Sandbox') : 'Not connected'}
                  size="small"
                  color={status.connected ? 'success' : 'warning'}
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 10, sm: 11 },
                    height: { xs: 20, sm: 24 },
                    borderRadius: 1.5,
                    boxShadow: status.connected ? `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}` : 'none',
                  }}
                />
              </Stack>
              {!isInbox && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500, fontFamily: 'Inter, sans-serif', fontSize: { xs: 12, sm: 14 } }}>
                  Chat with customers and sellers, run broadcasts, manage templates and automate replies — all from one premium inbox.
                </Typography>
              )}
            </Box>
          </Stack>
        </Card>
      )}

      {/* Glassmorphic Tabs Navigation */}
      {!isChatOpenOnMobile && (
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: isInbox ? { xs: 1.25, sm: 2 } : { xs: 2, sm: 3 },
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha('#ffffff', 0.6),
          backdropFilter: 'blur(10px)',
          borderRadius: 2.5,
          px: { xs: 0.5, sm: 1 },
          border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
        }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { bgcolor: WA_TEAL, height: 3, borderRadius: 3 } }}
            sx={{
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTabs-scroller': {
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              },
              '& .MuiTabs-scrollButtons': {
                color: 'primary.main',
                borderRadius: 1,
                width: 28,
              },
              '& .MuiTab-root': {
                minHeight: { xs: 40, sm: 48 },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: 11.5, sm: 13.5 },
                fontFamily: 'Inter, sans-serif',
                color: 'text.secondary',
                px: { xs: 1.25, sm: 2.5 },
                py: 0.75,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                  borderRadius: 2,
                },
                '&.Mui-selected': {
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                  fontWeight: 800,
                }
              }
            }}
          >
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <Tab
                  key={t.key}
                  iconPosition="start"
                  icon={<Icon size={15} style={{ marginRight: 4 }} />}
                  label={t.label}
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      {/* Active panel — consistent min-height so switching tabs never jumps */}
      <Box sx={{ pb: isChatOpenOnMobile ? 0 : { xs: 2, sm: 4 }, minHeight: isChatOpenOnMobile ? 'none' : WA_PANEL_MIN_H }}>
        <Active />
      </Box>
    </Container>
  );
};

const WhatsAppView = () => {
  return (
    <WaSettingsProvider>
      <WhatsAppViewContent />
    </WaSettingsProvider>
  );
};

export default WhatsAppView;
